import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Filter, 
  Search, 
  Grid, 
  List, 
  SortAsc, 
  SortDesc,
  Clock,
  TrendingUp,
  Star
} from 'lucide-react';
import NFTCard from '../components/NFTCard';

const Marketplace = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [filterPrice, setFilterPrice] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const mockNFTs = [
    {
      id: 1,
      name: "Supreme Genesis #1",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.1",
      currency: "ETH",
      creator: "0x1234...5678",
      likes: 42,
      views: 128,
      isAuction: false,
      timeLeft: null
    },
    {
      id: 2,
      name: "Digital Crown #5",
      image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop",
      price: "0.25",
      currency: "ETH",
      creator: "0x8765...4321",
      likes: 38,
      views: 95,
      isAuction: true,
      timeLeft: "2h 15m"
    },
    {
      id: 3,
      name: "Royal Artifact #12",
      image: "https://images.unsplash.com/photo-1642790105077-9c5a4a5b5b5b?w=400&h=400&fit=crop",
      price: "0.5",
      currency: "SUP",
      creator: "0xabcd...efgh",
      likes: 67,
      views: 203,
      isAuction: false,
      timeLeft: null
    },
    {
      id: 4,
      name: "Supreme Collection #8",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.15",
      currency: "ETH",
      creator: "0x5678...9abc",
      likes: 29,
      views: 87,
      isAuction: false,
      timeLeft: null
    },
    {
      id: 5,
      name: "Cosmic Warrior #23",
      image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop",
      price: "0.8",
      currency: "ETH",
      creator: "0xdef0...1234",
      likes: 89,
      views: 312,
      isAuction: true,
      timeLeft: "5h 42m"
    },
    {
      id: 6,
      name: "Neon Dreams #7",
      image: "https://images.unsplash.com/photo-1642790105077-9c5a4a5b5b5b?w=400&h=400&fit=crop",
      price: "0.3",
      currency: "SUP",
      creator: "0x9876...5432",
      likes: 156,
      views: 445,
      isAuction: false,
      timeLeft: null
    }
  ];

  const filteredNFTs = mockNFTs.filter(nft => {
    const matchesSearch = nft.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPrice = filterPrice === 'all' || 
      (filterPrice === 'low' && parseFloat(nft.price) < 0.2) ||
      (filterPrice === 'medium' && parseFloat(nft.price) >= 0.2 && parseFloat(nft.price) < 0.5) ||
      (filterPrice === 'high' && parseFloat(nft.price) >= 0.5);
    
    return matchesSearch && matchesPrice;
  });

  const sortedNFTs = [...filteredNFTs].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return parseFloat(a.price) - parseFloat(b.price);
      case 'price-high':
        return parseFloat(b.price) - parseFloat(a.price);
      case 'likes':
        return b.likes - a.likes;
      case 'views':
        return b.views - a.views;
      default:
        return b.id - a.id; // newest first
    }
  });

  return (
    <div className="min-h-screen py-8">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold text-white mb-2">NFT Marketplace</h1>
        <p className="text-gray-400">Discover and trade extraordinary NFTs</p>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10"
      >
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search NFTs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            {/* Price Filter */}
            <select
              value={filterPrice}
              onChange={(e) => setFilterPrice(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Prices</option>
              <option value="low">Under 0.2 ETH</option>
              <option value="medium">0.2 - 0.5 ETH</option>
              <option value="high">Over 0.5 ETH</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="newest">Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="likes">Most Liked</option>
              <option value="views">Most Viewed</option>
            </select>

            {/* View Mode */}
            <div className="flex bg-white/10 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
      >
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total NFTs</p>
              <p className="text-2xl font-bold text-white">{mockNFTs.length}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-400" />
          </div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active Auctions</p>
              <p className="text-2xl font-bold text-white">{mockNFTs.filter(nft => nft.isAuction).length}</p>
            </div>
            <Clock className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Volume</p>
              <p className="text-2xl font-bold text-white">2.1 ETH</p>
            </div>
            <Star className="h-8 w-8 text-yellow-400" />
          </div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Collections</p>
              <p className="text-2xl font-bold text-white">6</p>
            </div>
            <Filter className="h-8 w-8 text-purple-400" />
          </div>
        </div>
      </motion.div>

      {/* NFT Grid */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className={`grid gap-6 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
            : 'grid-cols-1'
        }`}
      >
        {sortedNFTs.map((nft, index) => (
          <motion.div
            key={nft.id}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 + index * 0.1 }}
          >
            <NFTCard nft={nft} />
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {sortedNFTs.length === 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center py-16"
        >
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-12 border border-white/10">
            <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No NFTs Found</h3>
            <p className="text-gray-400 mb-6">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterPrice('all');
                setSortBy('newest');
              }}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
            >
              Clear Filters
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Marketplace;
